import {
  registerGridOverride,
  type CellDecorator,
  type CellDecoratorContext,
  type GridOverride,
  type GridOverrideDeps,
} from "../../components/dataGrid/gridOverrides.tsx";
import { getPagePath } from "../../pages/pageRegistry.ts";

/**
 * transactionSearch grid: the sProductInfo cell is built server-side from
 * dbo.Web_Access_Transaction_Search_View (CASE on CTR.iPriceTypeID). The
 * column ships with a HYPERLINKPARAMS DisplayType + LinkID 214 whose
 * dispatcher only handles `#<id> Name`-style product rows; promotion rows
 * (no `#`) leak the display text into the edit param and parseInt picks
 * "50" from "50% off [CODE]". This decorator inspects iPriceTypeID per
 * row and dispatches to the right modal via PagePopup. DataGrid suppresses
 * the built-in HYPERLINK onClick when a cellDecorator is registered for
 * the column, so our wrapping span actually owns the click.
 *
 *  | iPriceTypeID | Label                  | Target                              |
 *  |--------------|------------------------|-------------------------------------|
 *  | 6            | Promotion [code]       | generatecodes?edit={iProductID}     |
 *  | 7            | Manual top up          | —                                   |
 *  | 8            | Automatic Top Up       | —                                   |
 *  | 9            | Subscription           | —                                   |
 *  | 23           | Package (PD)           | packagedeal?edit={iProductID}       |
 *  | 36           | Subsidy (PD)           | subsidize?edit={iProductID}         |
 *  | 40           | External / Flash Deal  | — (sGlobalEntityID, no editor)      |
 *  | 42           | External tender        | — (sGlobalEntityID, no editor)      |
 *  | else         | Product                | searcheditproduct?edit={iProductID} |
 *
 * For type 6, CTR.iProductID is the iPromotionCodeID (per the view's
 * DVD_PromotionCodes join on iProductID = iPromotionCodeID), so the code
 * editor is the natural target. For 23/36 the view's join on S_PackageDeal
 * makes iProductID the deal id; the default case is Access_Product.ID.
 */

function col(ctx: CellDecoratorContext, name: string): unknown {
  const lc = name.toLowerCase();
  for (const [k, idx] of Object.entries(ctx.colIndex)) {
    if (k.toLowerCase() === lc) return ctx.rowData[idx];
  }
  return undefined;
}

function appendEdit(basePath: string, id: number): string {
  const sep = basePath.includes("?") ? "&" : "?";
  return `${basePath}${sep}edit=${encodeURIComponent(String(id))}`;
}

/** Resolves the popup path for a given priceType + productId, or null when
 *  the row has no editor (top-up / subscription / external entity rows). */
function resolveTarget(priceType: number, productId: number): string | null {
  if (!Number.isFinite(productId) || productId <= 0) return null;
  switch (priceType) {
    case 6:
      return appendEdit(getPagePath("generateCodes"), productId);
    case 23:
      return appendEdit(getPagePath("packagedeal"), productId);
    case 36:
      return appendEdit(getPagePath("subsidize"), productId);
    case 7:
    case 8:
    case 9:
    case 40:
    case 42:
      return null;
    default:
      return appendEdit(getPagePath("searchEditProduct"), productId);
  }
}

function productInfoDecorator(deps: GridOverrideDeps): CellDecorator {
  return (node, ctx) => {
    const priceType = Number(col(ctx, "iPriceTypeID") ?? 0);
    const productId = Number(col(ctx, "iProductID") ?? 0);
    const target = resolveTarget(priceType, productId);
    if (!target) return node;

    return (
      <span
        role="link"
        tabIndex={0}
        style={{
          color: "var(--color-info)",
          textDecoration: "underline",
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          deps.openPopup(target);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            deps.openPopup(target);
          }
        }}
      >
        {node}
      </span>
    );
  };
}

function transactionSearchOverride(deps: GridOverrideDeps): GridOverride {
  return { cellDecorators: { sProductInfo: productInfoDecorator(deps) } };
}

registerGridOverride("transactionSearch", transactionSearchOverride);
